import { DateTime } from 'luxon';
import queryString from 'query-string';
import callApi, { CallApiResult, getCustomerId } from 'src/api';
import { RegionKeys } from 'src/core/provider/AppMeta';
import { PageableQueryParams } from 'src/entities/basic-types/Pageable';
import { PersonArch } from 'src/entities/organization/Person';
import {
    SCHEMA_SUCCESS_LIST_APPOINTMENT_ADD_SHAPE,
    SCHEMA_SUCCESS_LIST_CALL_ADD_SHAPE,
    SCHEMA_SUCCESS_LIST_CONTROL_SHAPE,
    SCHEMA_SUCCESS_LIST_ENTRY_SHAPE,
    SCHEMA_SUCCESS_LIST_NOTE_ADD_SHAPE,
} from 'src/schemas/SuccessList';

/* Fetch Success List Overview */

export const fetchSuccessListOverview = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelist/summary';
    return callApi(endpoint);
};

/* Fetch Success List Topics */

export const fetchSuccessListTopics = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelistTopics';
    return callApi(endpoint);
};

/* Fetch Success List */

export const fetchSuccessList = (
    topic: string,
    pageable: PageableQueryParams = {},
    personArch?: PersonArch
): Promise<CallApiResult> => {
    let query = queryString.stringify({ ...pageable, suggestion: personArch }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const customerId = getCustomerId();
    const endpoint = `/api/partner/${customerId}/namelist/${topic}${query}`;

    return callApi(endpoint, { method: 'GET' });
};

/* Add Note */

export const successListNoteAdd = (data: SCHEMA_SUCCESS_LIST_NOTE_ADD_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/note';

    return callApi(endpoint, { method: 'POST', data });
};

/* Add Call */

export const successListCallAdd = (raw: SCHEMA_SUCCESS_LIST_CALL_ADD_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/call';

    return callApi(endpoint, {
        method: 'POST',
        data: {
            ...raw,
            followUpAppointmentDate:
                raw.followUpAppointmentDate instanceof Date
                    ? DateTime.fromJSDate(raw.followUpAppointmentDate).toISO()
                    : undefined,
        },
    });
};

/* Control */

export const successListControl = (raw: SCHEMA_SUCCESS_LIST_CONTROL_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/control';

    return callApi(endpoint, {
        method: 'POST',
        data: {
            ...raw,
            registeredCandidateId: Number(raw.registeredCandidateId),
            followUpAppointmentDate:
                raw.followUpAppointmentDate instanceof Date
                    ? DateTime.fromJSDate(raw.followUpAppointmentDate).toISO()
                    : undefined,
        },
    });
};

/* Fetch Direct Customers */

export const fetchDirectCustomers = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/direct-customers';

    return callApi(endpoint, { method: 'GET' });
};

/* Add / Edit Success */

export const successListEntry = (raw: SCHEMA_SUCCESS_LIST_ENTRY_SHAPE, patch?: boolean): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/nameListEntry';

    return callApi(endpoint, {
        method: patch ? 'PUT' : 'POST',
        data: {
            ...raw,
            dateOfBirth: raw.dateOfBirth instanceof Date ? DateTime.fromJSDate(raw.dateOfBirth).toISODate() : undefined,
            email: raw.email !== '' ? raw.email : undefined,
            address: raw.address !== '' ? raw.address : undefined,
            phoneNumber: raw.phoneNumber && raw.phoneNumber.length > 4 ? raw.phoneNumber : undefined,
            familiarity: Number(raw.familiarity),
        },
    });
};

/* Create Org-Dashboard Contact ("Kontakt anlegen") */

export interface CreateOrgContactData {
    firstName: string;
    lastName: string;
    company?: string;
    email?: string;
    phoneNumber?: string;
    note?: string;
    /** Lowercase ISO2 of an active country (Länderverwaltung). */
    countryIso2: string;
    referrerCustomerId?: number;
}

export const createOrgContact = (
    data: CreateOrgContactData,
    partnerId: string | number = getCustomerId()
): Promise<CallApiResult<string>> => {
    const endpoint = '/api/partner/' + partnerId + '/nameListEntry';

    return callApi<string>(endpoint, {
        method: 'POST',
        data: {
            firstName: data.firstName,
            lastName: data.lastName,
            company: data.company || undefined,
            email: data.email || undefined,
            phoneNumber: data.phoneNumber || undefined,
            note: data.note || undefined,
            countryIso2: data.countryIso2,
            referrerCustomerId: data.referrerCustomerId,
            suggestion: 'Customer',
            candidateStatus: 'UNKNOWN',
            familiarity: 1,
        },
    });
};

/* Send a registration-invite email to a contact (no account is created until the invitee confirms) */

export interface SendContactInviteData {
    nameListEntryId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    // customer|sis|officeprofi — mapped by userservice to the Keycloak role granted at registration.
    inviteType?: string;
    // FB1-INVITE (Q781=B): a catalog role as invite type — wins over inviteType when set; since
    // R48-3 released to the invitee at registration (the auto-assign role is granted).
    inviteRoleId?: string;
    // R48-3: the Vermittler percentage (Freigaben › Vermittler step)
    referrerPercent?: number;
}

export const sendContactRegistrationInvite = (
    data: SendContactInviteData,
    partnerId: string | number = getCustomerId()
): Promise<CallApiResult> => {
    // The identifier names the inviting partner: the organisation shown in the admin panel, the
    // impersonated id in the old admin, 'current' for a logged-in partner.
    return callApi('/api/contact-invite/' + partnerId, { method: 'POST', data });
};

/**
 * Q760 invite-type options derived from the inviter's partner-role grant matrix (gf-settings).
 * Behind `roleGrantMatrix.enabled` — flag OFF answers 404 and the dialog keeps its hardcoded
 * Bestand options. Same inviter-identifier convention as the send above.
 */
export const getGrantableInviteTypes = (): Promise<CallApiResult<string[]>> => {
    return callApi('/api/organization/' + getCustomerId() + '/grantableInviteTypes');
};

export interface InviteRoleOption {
    // a partner_role id; null on the Kunde floor entry while no role is flagged auto-assign
    roleId: string | null;
    // 'customer' on the floor / auto-assign entry, null on releasable roles
    slug: string | null;
    // catalog name (PM-maintained display label), null on the Kunde floor entry
    name: string | null;
    // R48-3: granted at every registration — nothing is released
    autoAssigned?: boolean;
    // R48-3: the Zusatzrolle Vermittler — released with a percentage
    referral?: boolean;
}

/**
 * FB1-INVITE (Q781=B): the full option list — the Kunde floor plus every catalog role the
 * inviter's grant matrix allows. Behind the same `roleGrantMatrix.enabled` flag — 404 while OFF,
 * and the dialog falls back to the slug feed / Bestand options.
 */
export const getGrantableInviteRoles = (): Promise<CallApiResult<InviteRoleOption[]>> => {
    return callApi('/api/organization/' + getCustomerId() + '/grantableInviteRoles');
};

/** R48-3: the Registrierungsfreigabe costs of `roleId` for a contact who registers with the invite. */
export const getInviteRoleReleasePreview = (roleId: string): Promise<CallApiResult> =>
    callApi('/api/organization/' + getCustomerId() + '/inviteRoleReleasePreview', { params: { roleId } });

/** R48-3: the company fee and documents of the Vermittler step for a contact who registers with the invite. */
export const getInviteReferralPartner = (): Promise<CallApiResult> =>
    callApi('/api/organization/' + getCustomerId() + '/inviteReferralPartner');

/* Add Appointment */

export const successListAppointmentAdd = (raw: SCHEMA_SUCCESS_LIST_APPOINTMENT_ADD_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/appointment';

    return callApi(endpoint, {
        method: 'POST',
        data: {
            ...raw,
            // Absent = the Termin-Protokoll close; academy keeps the open row's Ort then.
            isOnline: raw.isOnline == null ? undefined : raw.isOnline === 'true',
            followUpAppointmentDate:
                raw.followUpAppointmentDate instanceof Date
                    ? DateTime.fromJSDate(raw.followUpAppointmentDate).toISO()
                    : undefined,
        },
    });
};

/* Fetch single name-list entry (rehydrates the full SuccessListEntry for the to-do edit dialogs) */

export const fetchSuccessListEntry = (nameListEntryId: string): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = `/api/partner/${customerId}/nameListEntry/${nameListEntryId}`;

    return callApi(endpoint, { method: 'GET' });
};

const NAME_LIST_ENTRY_FIELDS = [
    'nameListEntryId',
    'salutation',
    'firstName',
    'lastName',
    'address',
    'dateOfBirth',
    'phoneNumber',
    'countryIso2',
    'suggestion',
    'familiarity',
    'candidateStatus',
];

/** R48-3: stores the invite e-mail on the contact — academy's update replaces the whole entry. */
export const updateSuccessListEntryEmail = async (nameListEntryId: string, email: string): Promise<CallApiResult> => {
    const entryResult = await fetchSuccessListEntry(nameListEntryId);
    const entry = entryResult.response?.data;
    if (entryResult.error || !entry) return entryResult;
    const data = NAME_LIST_ENTRY_FIELDS.reduce<Record<string, unknown>>(
        (fields, key) => ({ ...fields, [key]: entry[key] }),
        { email }
    );
    return callApi('/api/partner/' + getCustomerId() + '/nameListEntry', { method: 'PUT', data });
};

/* History */

export const fetchSuccessListEntryHistory = (nameListEntryId: string) => {
    const customerId = getCustomerId();
    const endpoint = `/api/partner/${customerId}/nameListEntry/${nameListEntryId}/history`;

    return callApi(endpoint, { method: 'GET' });
};

/* Fetch Success List Regions */

export const fetchSuccessListRegions = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelistCountries';
    return callApi(endpoint, { method: 'GET' });
};

/* Set Success List Regions */

export const submitSelectSuccessListRegions = (data: RegionKeys[]): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelistCountries';
    return callApi(endpoint, { method: 'PUT', data });
};

export interface SendOfferInviteData {
    offerPublicId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    nameListEntryId?: string;
}

/** Offer invite (option 3): mails a prospect the public token→offer link. */
export const sendOfferInvite = (data: SendOfferInviteData): Promise<CallApiResult> => {
    return callApi('/api/contact-invite/' + getCustomerId() + '/offer', { method: 'POST', data });
};

export interface PublicOfferView {
    publicId: string;
    state: string;
    partnerName: string | null;
    preparedOn: string;
    totalAmount: number;
    currencyIso4217: string;
    gross: boolean;
    lines: { productNumber: number | null; name: string | null; quantity: number; nettPrice: number }[];
    inviteEmail: string | null;
    inviteFirstName: string | null;
    inviteLastName: string | null;
    invitePhone: string | null;
    inviteToken: string;
}

/** PUBLIC: the token→offer view of the /welcome/angebot page (no auth). */
export const fetchPublicOffer = (token: string): Promise<CallApiResult<PublicOfferView>> => {
    return callApi('/api/offerInvite/' + token);
};

/** PUBLIC: the prospect declines — the offer moves to 'cancelled'. */
export const denyPublicOffer = (token: string): Promise<CallApiResult<void>> => {
    return callApi('/api/offerInvite/' + token + '/deny', { method: 'POST' });
};
